import { NavLink } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { X, Home, Users, Wallet, FileText, Settings, DollarSign, TrendingUp } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Logo } from '@/components/ui'

interface SidebarProps {
  open: boolean
  onClose: () => void
}

interface NavItem {
  to: string
  icon: React.ReactNode
  label: string
}

export function Sidebar({ open, onClose }: SidebarProps) {
  const { t } = useTranslation()

  const navItems: NavItem[] = [
    {
      to: '/dashboard',
      icon: <Home className="h-5 w-5" />,
      label: t('nav.dashboard'),
    },
    {
      to: '/members',
      icon: <Users className="h-5 w-5" />,
      label: t('nav.members'),
    },
    {
      to: '/contributions',
      icon: <Wallet className="h-5 w-5" />,
      label: t('nav.contributions'),
    },
    {
      to: '/loans',
      icon: <DollarSign className="h-5 w-5" />,
      label: t('nav.loans'),
    },
    {
      to: '/distributions',
      icon: <TrendingUp className="h-5 w-5" />,
      label: t('nav.distributions'),
    },
    {
      to: '/reports',
      icon: <FileText className="h-5 w-5" />,
      label: t('nav.reports'),
    },
    {
      to: '/settings',
      icon: <Settings className="h-5 w-5" />,
      label: t('nav.settings'),
    },
  ]

  return (
    <>
      {/* Mobile backdrop */}
      {open && (
        <div
          className="fixed inset-0 z-40 bg-gray-600 bg-opacity-75 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transition-transform lg:static lg:translate-x-0',
          open ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex h-full flex-col">
          {/* Mobile header */}
          <div className="flex h-16 items-center justify-between border-b border-gray-200 px-4 lg:hidden">
            <Logo variant="horizontal" size="sm" />
            <button
              onClick={onClose}
              className="rounded-md p-2 text-gray-600 hover:bg-gray-100 hover:text-gray-900"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 space-y-1 px-3 py-4">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => onClose()}
                className={({ isActive }: { isActive: boolean }) =>
                  cn(
                    'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                  )
                }
              >
                {item.icon}
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>
      </div>
    </>
  )
}